import * as React from "react";
import DashboardLayout from "../layouts/DashboardLayout";
import ReportDetail from "../components/ReportDetail";
import { UserAuth } from "../context/AuthContext";
import { Link as RouterLink, useParams } from "react-router-dom";
import Link from "@mui/joy/Link";
import Typography from "@mui/joy/Typography";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import ThumbDownAltIcon from "@mui/icons-material/ThumbDownAlt";
import { toast } from "react-toastify";
import { db } from "../FirebaseConfig";
import DialogTitle from "@mui/joy/DialogTitle";
import DialogContent from "@mui/joy/DialogContent";
import DialogActions from "@mui/joy/DialogActions";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import Divider from "@mui/joy/Divider";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import Input from "@mui/joy/Input";

interface RouteParams {
  [key: string]: string | undefined;
  id: string;
}

export default function ReportDetailPage() {
  const { userData } = UserAuth();
  const [mounted, setMounted] = React.useState(false);
  const { id } = useParams<RouteParams>();
  const [open, setOpen] = React.useState(false);
  const [score, setScore] = React.useState<string | number>("");

  React.useEffect(() => {
    setMounted(true);
    if (mounted) {
      if (
        userData?.role !== "organizer" &&
        userData?.role !== "admin" &&
        userData?.role !== "super"
      ) {
        window.location.href = "/unauthorized";
      }
    }
  }, []);

  const handleAccept = async () => {
    const parsedScore = Number(score);
    if (score === "" || isNaN(parsedScore) || parsedScore < 0 || parsedScore > 100) {
      toast.error("Please enter a valid score between 0 and 100");
      return;
    }
  
    try {
      const eventsSnapshot = await db.collection("Events").get();
  
      for (const eventDoc of eventsSnapshot.docs) {
        const reportRef = eventDoc.ref.collection("Reports").doc(id);
        const statsRef = eventDoc.ref.collection("Statistics").doc("reportStats");
        const docSnapshot = await reportRef.get();
  
        if (docSnapshot.exists) {
          const currentReportData = docSnapshot.data();
          const currentStatus = currentReportData?.status || "In Review";
  
          await reportRef.update({
            status: "Accepted",
            score: parsedScore,
          });
  
          await db.runTransaction(async (transaction) => {
            const statsDoc = await transaction.get(statsRef);
  
            if (statsDoc.exists) {
              const currentData = statsDoc.data();
              
              // Create a new update object
              const updateStats: { [key: string]: number } = {
                Accepted: (currentData?.Accepted || 0) + 1,
              };
  
              // Adjust counts based on previous status
              if (currentStatus === "In Review") {
                updateStats["In Review"] = Math.max((currentData?.["In Review"] || 1) - 1, 0);
              } else if (currentStatus === "Rejected") {
                updateStats["Rejected"] = Math.max((currentData?.Rejected || 0) - 1, 0);
                updateStats["In Review"] = (currentData?.["In Review"] || 0) + 1;
              }
  
              transaction.update(statsRef, updateStats);
            }
          });
  
          toast.success("Report Accepted");
  
          setTimeout(() => {
            window.location.reload();
          }, 5000);
          return;
        }
      }
  
      toast.error("Report not found");
    } catch (error) {
      console.error("Error accepting report: ", error);
      toast.error("Error accepting report");
    }
  };
  
  const handleReject = async () => {
    try {
      const eventsSnapshot = await db.collection("Events").get();
  
      for (const eventDoc of eventsSnapshot.docs) {
        const reportRef = eventDoc.ref.collection("Reports").doc(id);
        const statsRef = eventDoc.ref.collection("Statistics").doc("reportStats");
        const docSnapshot = await reportRef.get();
  
        if (docSnapshot.exists) {
          const currentReportData = docSnapshot.data();
          const currentStatus = currentReportData?.status || "In Review";
  
          await reportRef.update({
            status: "Rejected",
            score: 0,
          });
  
          await db.runTransaction(async (transaction) => {
            const statsDoc = await transaction.get(statsRef);
  
            if (statsDoc.exists) {
              const currentData = statsDoc.data();
              
              // Create a new update object
              const updateStats: { [key: string]: number } = {
                Rejected: (currentData?.Rejected || 0) + 1,
              };
  
              // Adjust counts based on previous status
              if (currentStatus === "In Review") {
                updateStats["In Review"] = Math.max((currentData?.["In Review"] || 1) - 1, 0);
              } else if (currentStatus === "Accepted") {
                updateStats["Accepted"] = Math.max((currentData?.Accepted || 0) - 1, 0);
                updateStats["In Review"] = (currentData?.["In Review"] || 0) + 1;
              }
  
              transaction.update(statsRef, updateStats);
            }
          });
  
          toast.success("Report Rejected");
  
          setTimeout(() => {
            window.location.reload();
          }, 5000);
          return;
        }
      }
  
      toast.error("Report not found");
    } catch (error) {
      toast.error("Error rejecting report");
    }
  };

  return (
    <>
      <DashboardLayout
        title={`Report ${id}`}
        navigator={
          <Link
            underline="none"
            color="neutral"
            component={RouterLink}
            to="/evaluatereport"
            aria-label="Home"
          >
            <Typography fontWeight={500} fontSize={12}>
              Report Evaluation
            </Typography>
          </Link>
        }
        visible={true}
        button={
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <Button
              startDecorator={<ThumbUpAltIcon />}
              variant="outlined"
              color="success"
              onClick={() => setOpen(true)}
              size="sm"
            >
              Accept Report
            </Button>
            <Button
              onClick={() => {
                handleReject();
              }}
              variant="outlined"
              color="danger"
              startDecorator={<ThumbDownAltIcon />}
              size="sm"
            >
              Reject Report
            </Button>
          </Box>
        }
      >
        <ReportDetail />
      </DashboardLayout>
      <div>
        <Modal open={open} onClose={() => setOpen(false)}>
          <ModalDialog variant="outlined" role="alertdialog">
            <DialogTitle>
              <WorkspacePremiumIcon />
              Report Evaluation Score
            </DialogTitle>
            <Divider />
            <DialogContent>
              Please provide the evaluation score for this report.
            </DialogContent>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                justifyContent: "center",
              }}
            >
              <Input
                placeholder="Enter Score"
                value={score}
                onChange={(e) => setScore(e.target.value)}
                sx={{
                  width: "40%",
                  textAlign: "center",
                  "& input": {
                    textAlign: "center",
                    fontSize: "20px",
                    fontWeight: "600",
                  },
                }}
              />
              <Typography level="h3">
                /<span style={{ marginLeft: "6px" }}> 100 </span>
              </Typography>
            </Box>
            <DialogActions>
              <Button variant="plain" color="success" onClick={handleAccept}>
                Submit
              </Button>
              <Button
                variant="plain"
                color="neutral"
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
            </DialogActions>
          </ModalDialog>
        </Modal>
      </div>
    </>
  );
}
